import {
    QueryClient,
    QueryClientProvider,
    useMutation,
    useQuery,
} from '@tanstack/react-query'
import { CampaignList } from './components/CampaignList'
import { ReactNode, useEffect, useState } from 'react'
import { Campaign, CampaignDetail } from './interfaces/Campaign.interface'
import { CampaignEditor } from './components/CampaignEditor'
import { APIService } from './services/API.service'
import { Application } from './interfaces/Application.interface'
import { App, Button, Dropdown, Flex, Layout, Menu, MenuProps, Space, Typography } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { Dashboard } from './pages/dashboard'
import { MenuInfo } from 'rc-menu/lib/interface'
import { Audiences } from './pages/audiences'
import { Settings } from './pages/settings'
import { AuthProvider, PushRole, useAuth } from './hooks/useAuth'
import { CampaignDevTools } from './components/CampaignDevTools'
import { DevTools } from './pages/devtools'
import { Devices } from './pages/devices'
import { Breadcrumb } from 'react-instantsearch/dist/es/ui/Breadcrumb'
import { BreadcrumbConnectorParamsItem } from 'instantsearch.js/es/connectors/breadcrumb/connectBreadcrumb'
import { NamePath } from 'antd/es/form/interface'
import { Push } from './pages/push'
import { SystemOrganisations } from './pages/system_organsations'


// begin hack to register dayjs overrides
// ref: https://stackoverflow.com/questions/76650410/antd-dayjs-typeerror-clone-weekday-is-not-a-function
// expect that antd will at some point incorporate this into their App component or similar for now.. 
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import {ApplicationDetail} from "./interfaces/OrgDetail.interface";


dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
// end hack to register dayjs overrides

const { Header, Content, Sider } = Layout;

const queryClient = new QueryClient()

export const WrapperApp = () => {
    return (
        <App>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <OrgAppSelector />
                </AuthProvider>
            </QueryClientProvider>
        </App>
    )
}


const getMenuLabel = (label:string)=>(<Flex justify="space-between">
    <Typography.Text>{label}</Typography.Text>
</Flex>)
export const OrgAppSelector = () => {
    const [selectedApp, setSelectedApp] = useState<Application | null>(null)
    const [menu, setMenu] = useState<MenuProps['items']>([])
    const [currentContent, setCurrentContent] = useState<ReactNode>(null)
    const auth = useAuth();
    const [currentContextKey, setCurrentContentKey] = useState<string|null>(null);
    const applicationQuery = useQuery({ queryKey: ['org', 'applications',auth.api.authToken], queryFn: async () => await auth.api.getOrgApplications("test") })
   
    const handleApplicationSelection: MenuProps['onClick'] = ({ key }) => {
        let selectedApplication = (applicationQuery.data??[]).find(x=>x.id == key);
        if(selectedApplication)
        {
            setSelectedApp(selectedApplication);
        }
      };
      useEffect(()=>{
        if(selectedApp)
        {
            // console.log('update view', selectedApp.name, currentContextKey);
            let newView:ReactNode|null = <Dashboard applicationId={selectedApp.id} />
            switch(currentContextKey)
            {
                case "dashboard":
                    newView =  <Dashboard applicationId={selectedApp.id} />
                    break;
                case "audience":
                case "audience:segments":
                    newView = <Audiences applicationId={selectedApp.id} />
                    break;
                case "audience:devices":
                    newView = <Devices applicationId={selectedApp.id} />    
                    break;
                case "messages:push":
                case "delivery":
                    newView = <Push applicationId={selectedApp.id} searchConfig={selectedApp.searchConfiguration || null} />
                    break;
                case "settings":
                    newView = <Settings applicationId={selectedApp.id} />
                    break;
                case "messages:devtools":
                    newView = <DevTools applicationId={selectedApp.id} apiKey={selectedApp.apiKeys[0]?.id ?? ""} />
                    break;
                case "system:organsations":
                    newView = <SystemOrganisations />
                    break;
                default:
                    newView = null;
                    break;
            }

            if(newView)
                setCurrentContent(newView);
        }

    }, [currentContextKey, selectedApp])

    useEffect(() => {
        if (selectedApp == null && applicationQuery.data) {
            // only one app exists and the user hasn't selected an app yet
            // auto select the app
            let app = applicationQuery.data[0];
            if (app && applicationQuery.data.length > 0) {
                setSelectedApp(app)
                setCurrentContent(<Dashboard applicationId={app.id} />);
            }
        }

        if (applicationQuery.data && selectedApp) {

            let newMenu: MenuProps['items'] = [
                {
                    key: 'dashboard',
                    label: getMenuLabel("Dashboard")
                },
                {
                    key: 'messages',
                    label: getMenuLabel("Messages"),
                    children: [
                        {
                            key: 'messages:push',
                            label: getMenuLabel("Push")
                        },
                        auth.hasClaims(selectedApp?.id, PushRole.CanUseDeveloperTools) ? {
                            key: "messages:devtools",
                            label: getMenuLabel("Push DevTools")
                        } : null 
                    ]
                },
                {
                    key: 'audience',
                    label: getMenuLabel("Audience"),
                    children: [
                        {
                            key: 'audience:segments',
                            label: getMenuLabel('Segments')
                        },
                        {
                            key: 'audience:devices',
                            label: getMenuLabel('Devices')
                        }
                    ]
                },
                // auth.hasClaims(selectedApp?.id, PushRole.CanUseDeveloperTools) ? {
                //     key: 'settings',
                //     label: getMenuLabel('Settings')
                // }: null,
                auth.isSystemAdmin() ? {
                    key: 'system',
                    label: getMenuLabel('System'),
                    children: [
                        {
                            key: 'system:organsations',
                            label: getMenuLabel('Organisations')
                        }
                    ]
                }:null
            ];
            setMenu(newMenu);
        }
    }, [applicationQuery.data, selectedApp, auth.decodedToken])

    if (selectedApp == null) {
        const handleApplicationSelection = (app: ApplicationDetail) => {
            setSelectedApp(app);
        }

        if (applicationQuery.isLoading) {
            return (<>Fetching Apps...</>);
        }
        // if (applicationQuery.data) {
        //     return (
        //         <>
        //             <Flex vertical>
        //                 <Typography.Title level={2}>Select Application</Typography.Title>
        //                 {applicationQuery.data?.map(app => (<Button key={app.id} onClick={() => handleApplicationSelection(app)}>{app.name}</Button>))}
        //             </Flex>
        //         </>
        //     )
        // }
        if(applicationQuery.data) {
            return (<>Processing Data...</>)
        }
        return (<>No Data</>)

    } else {
       

        const menuClicked: MenuProps['onClick'] = (info:MenuInfo) => {
            if(info.key != 'appselector')
                setCurrentContentKey(info.key)
        }



        return (
            <>
                <Layout style={{width: '100vw', height: '100vh', boxSizing: 'border-box', flexDirection:'row'}}>
                    <Layout style={{width: '150pt', minWidth: '150pt',padding:0, margin:0}} hasSider>
                        <Sider 
                            width={'150pt'} // so that it auto adjusts 
                            style={{
                                overflowY: 'auto',
                                position: 'sticky',
                                insetInlineStart: 0,
                                top: 0,
                                bottom: 0,
                                scrollbarWidth: 'thin',
                                scrollbarGutter: 'stable',
                                background: 'white'
                            }}>
                            <Flex vertical style={{
                                height: '30pt',
                                padding: '10pt'
                            }}>

                            <Dropdown
                                menu={{
                                    items: (applicationQuery.data??[]).map(app=>{
                                        return {
                                            key: app.id,
                                            label: app.name
                                        }
                                    }),
                                    onClick: handleApplicationSelection,
                                }}
                                disabled={(applicationQuery.data??[]).length === 1}
                            >
                                <a onClick={(e) => e.preventDefault()}>
                                    <Flex justify="space-between">
                                        <Typography.Text>{selectedApp?.name ?? (applicationQuery.data && applicationQuery.data[0]?.name) ?? ""}</Typography.Text>
                                        <DownOutlined />
                                    </Flex>
                                </a>
                            </Dropdown>
                            </Flex>
                            <Menu
                                style={{width:'100%'}}
                                mode="inline"
                                defaultSelectedKeys={['dashboard']}
                                defaultOpenKeys={['messages', 'audience']}
                                items={menu} 
                                onClick={menuClicked}
                                />
                        </Sider>
                    </Layout>
                    <Layout style={{
                        width: 'calc(100vw - 150pt)', padding:10, margin:0,
                        overflow: 'auto',
                    }}>
                        <div>
                        <Content style={{ 
                            padding:0,margin:0,
                            }}> 
                           {currentContent}
                        </Content>
                        </div>
                    </Layout>
                </Layout>
            </>
        )
    }
}
